<template>
    <!-- Renders the loading spinner; replace with your actual Loading component -->
    <Loading
        :is-loading="isLoading"
        :showBar="showBar"
        :progress="loadingProgress"
        :label="currentMessage"
    />
</template>

<script>
import Loading from '@/components/Loading.vue'; // Path to your real Loading component

export default {
    name: 'LoadingManager',
    components: { Loading },
    data() {
        return {
            /**
             * Whether to show the loading spinner at all
             */
            isLoading: false,

            /**
             * How many steps (or tasks) total we expect. Used to compute increments in "increaseProgress"
             */
            loadingSteps: 1,

            /**
             * Current progress (0 to 100)
             */
            loadingProgress: 0,

            /**
             * Internally track active tasks. Each entry has { id, message, priority }.
             * The key is the auto-generated ID.
             */
            activeLoadingTasks: {},
            /**
             * Tracks the ID of the next task to be added.
             */
            nextTaskId: 0,
        };
    },
    computed: {
        /**
         * Derives which message to display from the currently active tasks.
         * The lowest priority number is considered the "highest priority".
         * If no tasks, returns null.
         */
        currentMessage() {
            const taskEntries = Object.entries(this.activeLoadingTasks);
            if (taskEntries.length === 0) return null;

            // Sort tasks by ascending priority
            taskEntries.sort(
                ([, taskA], [, taskB]) => taskA.priority - taskB.priority
            );

            // Return the message of the first (highest-priority) task
            const [, highestPriorityTask] = taskEntries[0];
            return highestPriorityTask.message;
        },

        /**
         * Whether to show the progress bar. Is true if there are more than 1 tasks.
         */
        showBar() {
            return this.loadingSteps > 1;
        },
    },
    methods: {
        /**
         * Start the loading spinner, reset progress, and optionally set how many steps are expected.
         * @param {Number} steps - total steps or tasks we plan on tracking.
         */
        start(steps = 1) {
            this.isLoading = true;
            this.loadingSteps = steps;
            this.loadingProgress = 0;

            // Optionally clear out old tasks if you want a fresh start
            this.activeLoadingTasks = {};
        },

        /**
         * Stop the loading spinner and set progress to 100.
         */
        stop() {
            this.isLoading = false;
            this.loadingProgress = 100;
        },

        /**
         * Add a loading task that will auto-increment progress upon completion.
         *
         * @param {String} message   - The label or text to display
         * @param {Number} priority  - Lower = higher priority (determines which message is shown)
         * @param {Promise} promise  - The async task promise
         *
         * @return {Promise}         - Returns the same promise to allow chaining
         */
        addTask(message, priority, promise) {
            // Generate a unique ID
            const taskId = this.generateTaskId();

            // Store the task
            this.activeLoadingTasks[taskId] = {
                id: taskId,
                message,
                priority,
            };

            console.debug(`Task ${taskId} added`);

            // When the promise resolves or rejects, increment progress & remove
            promise
                .then(() => {
                    console.debug(`Task ${taskId} completed`);
                    this.increaseProgress();
                    this.removeTask(taskId);
                })
                .catch(() => {
                    // Even on error, we remove the task and increment progress
                    console.error(`Task ${taskId} failed`);
                    this.increaseProgress();
                    this.removeTask(taskId);
                });

            return promise;
        },

        /**
         * Remove a completed task from the active tasks.
         * @param {String} taskId - The ID of the task to remove
         */
        removeTask(taskId) {
            delete this.activeLoadingTasks[taskId];
        },

        /**
         * Increase current progress by 1 "step". Computed as 100 / total steps.
         * Clamped at 100.
         */
        increaseProgress() {
            const stepIncrement = 100 / this.loadingSteps;
            this.loadingProgress = Math.min(
                this.loadingProgress + stepIncrement,
                100
            );
        },

        /**
         * Simple incremental ID generator.
         * Generates IDs like: "task-0", "task-1", etc.
         */
        generateTaskId() {
            const newId = `task-${this.nextTaskId}`;
            this.nextTaskId++;
            return newId;
        },
    },
};
</script>
